import type { ReactNode } from 'react'
import { scaleLinear, scalePoint } from '@visx/scale'
import { Group } from '@visx/group'
import { AxisBottom, AxisLeft } from '@visx/axis'
import { AreaClosed, LinePath, stack as visxStack, area as visxArea } from '@visx/shape'
import { curveLinear, curveMonotoneX, curveStep } from '@visx/curve'
import type { ChartResult, ChartSeriesPoint, ChartSpec } from '../../../types/chart.js'
import { CHART_AXIS_COLOR, CHART_GRID_COLOR, getSeriesStyle } from './chartTheme.js'
import type { ChartOverlayContext } from './chartOverlayTypes.js'

export interface AreaChartProps {
  spec: ChartSpec
  data: ChartResult
  width?: number
  height?: number
  className?: string
  testId?: string
  onSeriesClick?: (seriesKey: string) => void
  renderOverlay?: (ctx: ChartOverlayContext) => ReactNode
  stack?: 'absolute' | 'percent'
}

type StackRow = { x: string; [key: string]: string | number }
type StackPoint = [number, number] & { data: StackRow }
type StackSeries = Array<StackPoint> & { key: string }

export function AreaChart({
  spec,
  data,
  width = 500,
  height = 240,
  className = '',
  testId,
  onSeriesClick,
  renderOverlay,
  stack,
}: AreaChartProps) {
  const margin = { top: 20, right: 20, bottom: 40, left: 50 }
  const xMax = Math.max(0, width - margin.left - margin.right)
  const yMax = Math.max(0, height - margin.top - margin.bottom)

  const xKeys = Array.from(
    new Set(data.series.flatMap((s) => s.points.map((p) => String(p.x)))),
  )

  const xScale = scalePoint<string>({
    domain: xKeys,
    range: [0, xMax],
    padding: 0.5,
  })

  const curveType = spec.style?.curve === 'smooth'
    ? curveMonotoneX
    : (spec.style?.curve === 'step' ? curveStep : curveLinear)

  let yScale: ReturnType<typeof scaleLinear<number>>
  let stackedSeries: StackSeries[] | null = null

  if (stack) {
    const keys = data.series.map((s, idx) => s.key || String(idx))
    const rows: StackRow[] = xKeys.map((xKey) => {
      const row: StackRow = { x: xKey }
      data.series.forEach((s, sIdx) => {
        const k = s.key || String(sIdx)
        const pt = s.points.find((p) => String(p.x) === xKey)
        row[k] = pt ? pt.y : 0
      })
      return row
    })

    const stacker = visxStack<StackRow, string>({
      keys,
      offset: stack === 'percent' ? 'expand' : 'none',
    })
    stackedSeries = stacker(rows) as unknown as StackSeries[]

    if (stack === 'percent') {
      yScale = scaleLinear<number>({
        domain: [0, 1],
        range: [yMax, 0],
      })
    } else {
      const totals = rows.map((r) =>
        keys.reduce((sum, k) => sum + (Number(r[k]) || 0), 0),
      )
      const maxTotal = Math.max(...totals, 1)
      yScale = scaleLinear<number>({
        domain: [0, maxTotal],
        range: [yMax, 0],
        nice: true,
      })
    }
  } else {
    const allY = data.series.flatMap((s) => s.points.map((p) => p.y))
    const maxY = Math.max(...allY, 1)
    yScale = scaleLinear<number>({
      domain: [0, maxY],
      range: [yMax, 0],
      nice: true,
    })
  }

  const stackedAreaGen = stack
    ? visxArea<StackPoint>({
        x: (d) => xScale(String(d.data.x)) || 0,
        y0: (d) => yScale(Number.isFinite(d[0]) ? d[0] : 0),
        y1: (d) => yScale(Number.isFinite(d[1]) ? d[1] : 0),
        curve: curveType,
      })
    : null

  return (
    <svg
      width={width}
      height={height}
      className={`chartSvg ${className}`}
      data-testid={testId}
      role="img"
      aria-label={spec.title || 'Area Chart'}
    >
      <Group left={margin.left} top={margin.top}>
        {stack && stackedSeries ? (
          data.series.map((series, sIdx) => {
            const style = getSeriesStyle(sIdx, spec.style?.seriesColor?.[series.key])
            const seriesKey = series.key || String(sIdx)
            const isInteractive = Boolean(onSeriesClick)
            const seriesStack = stackedSeries?.[sIdx]
            const pathD = seriesStack && stackedAreaGen ? (stackedAreaGen(seriesStack) || '') : ''
            const topValFirstTick = seriesStack?.[0]?.[1] ?? 0
            const topEdgeFirstTick = yScale(topValFirstTick)

            return (
              <Group
                key={seriesKey}
                tabIndex={isInteractive ? 0 : undefined}
                role={isInteractive ? 'button' : undefined}
                aria-label={isInteractive ? (series.label || seriesKey) : undefined}
                style={{ cursor: isInteractive ? 'pointer' : undefined }}
                onClick={isInteractive ? () => onSeriesClick?.(seriesKey) : undefined}
                onKeyDown={isInteractive ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSeriesClick?.(seriesKey)
                  }
                } : undefined}
              >
                <path
                  className="band"
                  d={pathD}
                  fill={style.color}
                  stroke="var(--surface)"
                  strokeWidth={1}
                  data-series-key={seriesKey}
                  data-top-value={topValFirstTick}
                  data-top-edge={topEdgeFirstTick}
                  onClick={isInteractive ? () => onSeriesClick?.(seriesKey) : undefined}
                />
              </Group>
            )
          })
        ) : (
          data.series.map((series, sIdx) => {
            const style = getSeriesStyle(sIdx, spec.style?.seriesColor?.[series.key])
            const strokeDash = style.strokeDasharray || (spec.style?.lineStyle === 'dashed' ? '4 4' : undefined)
            const seriesKey = series.key || String(sIdx)
            const isInteractive = Boolean(onSeriesClick)

            return (
              <Group
                key={seriesKey}
                tabIndex={isInteractive ? 0 : undefined}
                role={isInteractive ? 'button' : undefined}
                aria-label={isInteractive ? (series.label || seriesKey) : undefined}
                style={{ cursor: isInteractive ? 'pointer' : undefined }}
                onClick={isInteractive ? () => onSeriesClick?.(seriesKey) : undefined}
                onKeyDown={isInteractive ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSeriesClick?.(seriesKey)
                  }
                } : undefined}
              >
                <AreaClosed<ChartSeriesPoint>
                  className={isInteractive ? 'band' : undefined}
                  data={series.points}
                  x={(p) => xScale(String(p.x)) || 0}
                  y={(p) => yScale(p.y)}
                  yScale={yScale}
                  fill={style.color}
                  fillOpacity={0.25}
                  curve={curveType}
                />
                <LinePath<ChartSeriesPoint>
                  data={series.points}
                  x={(p) => xScale(String(p.x)) || 0}
                  y={(p) => yScale(p.y)}
                  stroke={style.color}
                  strokeWidth={2}
                  strokeDasharray={strokeDash}
                  curve={curveType}
                />
              </Group>
            )
          })
        )}

        {renderOverlay?.({
          xScale,
          yScale,
          margin,
          innerWidth: xMax,
          innerHeight: yMax,
        })}

        {spec.style?.gridlines !== false && (
          <AxisBottom
            top={yMax}
            scale={xScale}
            tickValues={
              width < 480 && xKeys.length > 7
                ? xKeys.filter((_, idx) => idx % 2 === 0 || idx === xKeys.length - 1)
                : undefined
            }
            stroke={CHART_GRID_COLOR}
            tickStroke={CHART_GRID_COLOR}
            tickLabelProps={{
              fill: CHART_AXIS_COLOR,
              fontSize: 10,
              textAnchor: 'middle',
            }}
          />
        )}

        <AxisLeft
          scale={yScale}
          stroke={CHART_GRID_COLOR}
          tickStroke={CHART_GRID_COLOR}
          tickFormat={stack === 'percent' ? (val) => `${Math.round(Number(val) * 100)}%` : undefined}
          tickLabelProps={{
            fill: CHART_AXIS_COLOR,
            fontSize: 10,
            textAnchor: 'end',
            dx: -4,
          }}
        />
      </Group>
    </svg>
  )
}
